import { Link } from 'react-router-dom';
import type { StatusData, SaasModule } from '../page';

const CAT_COLOR: Record<string, string> = {
  Production: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Website:    'bg-cyan-50 text-cyan-700 border-cyan-200',
  Automation: 'bg-amber-50 text-amber-700 border-amber-200',
  Settings:   'bg-rose-50 text-rose-700 border-rose-200',
};

const MODULE_ICONS: Record<string, string> = {
  digital_invitations: 'ri-mail-send-line',    media_packages: 'ri-folder-image-line',
  dvd_online: 'ri-disc-line',                  ai_sales_assistant: 'ri-robot-line',
  appointments: 'ri-calendar-line',            whatsapp_notifications: 'ri-whatsapp-line',
  email_automations: 'ri-mail-settings-line',  custom_domain: 'ri-earth-line',
  photo_selection: 'ri-image-line',            final_deliveries: 'ri-send-plane-line',
};

function ModuleRow({ mod }: { mod: SaasModule }) {
  const catColor = CAT_COLOR[mod.category] ?? 'bg-slate-100 text-slate-500 border-slate-200';
  const icon     = MODULE_ICONS[mod.id] ?? 'ri-puzzle-line';

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className={`w-8 h-8 flex items-center justify-center rounded-lg border ${catColor} shrink-0`}>
        <i className={`${icon} text-sm`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-slate-700 text-sm font-medium">{mod.name}</p>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${catColor}`}>{mod.category}</span>
          {mod.is_core  && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200">Core</span>}
          {mod.is_addon && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-600 border border-violet-200">Add-on</span>}
        </div>
        <p className="text-slate-400 text-[10px] font-mono">{mod.id}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {mod.price_monthly > 0 && (
          <span className="text-amber-600 text-xs font-bold">${mod.price_monthly}<span className="text-slate-300 font-normal">/mo</span></span>
        )}
        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${
          mod.is_active
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-400 border-slate-200'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${mod.is_active ? 'bg-emerald-400' : 'bg-slate-300'}`}></span>
          {mod.is_active ? 'Active' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}

interface Props { data: StatusData; }

export default function ModulesStatusTab({ data }: Props) {
  const byCategory: Record<string, SaasModule[]> = {};
  data.allModules.forEach((m) => {
    (byCategory[m.category] = byCategory[m.category] ?? []).push(m);
  });

  return (
    <div className="space-y-6">

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Modules',   value: data.allModules.length,          icon: 'ri-puzzle-line',           color: 'text-blue-600 bg-blue-50' },
          { label: 'Active',          value: data.activeModulesCount,          icon: 'ri-checkbox-circle-line',  color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Enabled (all tenants)', value: data.tenantModulesEnabled, icon: 'ri-toggle-line',           color: 'text-violet-600 bg-violet-50' },
          { label: 'Pending Requests', value: data.moduleRequestsPending,      icon: 'ri-time-line',             color: 'text-amber-600 bg-amber-50' },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <span className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${s.color.split(' ')[1]}`}>
              <i className={`${s.icon} text-base ${s.color.split(' ')[0]}`}></i>
            </span>
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wider">{s.label}</p>
              <p className="text-slate-900 text-2xl font-bold">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Request pipeline ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-inbox-line text-slate-400 text-sm"></i>
            <h3 className="text-slate-700 text-sm font-semibold">Addon Request Pipeline</h3>
          </div>
          <Link to="/sa/module-requests" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium transition-colors">
            View all <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Pending',  value: data.moduleRequestsPending,  color: 'bg-amber-50 border-amber-200 text-amber-700',     dot: 'bg-amber-400',   linkTo: '/sa/module-requests' },
              { label: 'Approved', value: data.moduleRequestsApproved, color: 'bg-emerald-50 border-emerald-200 text-emerald-700', dot: 'bg-emerald-400' },
              { label: 'Rejected', value: data.moduleRequestsRejected, color: 'bg-red-50 border-red-200 text-red-600',             dot: 'bg-red-400' },
            ].map((s) => (
              <div key={s.label} className={`flex items-center gap-3 p-4 rounded-xl border ${s.color}`}>
                <span className={`w-3 h-3 rounded-full ${s.dot} shrink-0`}></span>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs font-medium opacity-70">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          {data.moduleRequestsPending > 0 && (
            <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <i className="ri-error-warning-line text-amber-500 text-sm"></i>
              <p className="text-amber-700 text-xs flex-1">
                <strong>{data.moduleRequestsPending} solicitud{data.moduleRequestsPending > 1 ? 'es' : ''}</strong> de add-on esperando revisión.
              </p>
              <Link to="/sa/module-requests" className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-black text-xs font-semibold rounded-lg transition-colors whitespace-nowrap">
                Review now
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Modules by category ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {Object.entries(byCategory).map(([category, mods]) => (
          <div key={category} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${CAT_COLOR[category] ?? 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  {category}
                </span>
                <span className="text-slate-400 text-xs">{mods.length} modules</span>
              </div>
              <span className="text-xs text-emerald-600 font-medium">
                {mods.filter((m) => m.is_active).length} active
              </span>
            </div>
            <div className="px-5 py-1">
              {mods.map((mod) => <ModuleRow key={mod.id} mod={mod} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
